import { styled } from "@monolith-forensics/monolith-ui";
import { getFileIconSrc, handleFileIconError } from "../utils/fileIcon";
import { File } from "../files.types";
import { useCaseFiles } from "../Hooks";
import { useState } from "react";
import { ManagedRowIcon } from "@/Features/EvidenceFiles/ManagedRowIcon";
import {
  isEvidenceFile,
  isManagedRow,
} from "@/Features/EvidenceFiles/evidence-files.utils";

const StyledContainer = styled.div`
  &.data-cell {
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    user-select: none;

    .label {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  &.data-cell.file:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.primary.main};
    cursor: grab;
  }

  &.data-cell.file:active {
    cursor: grabbing;
  }

  // Managed rows can't be moved (ENG-2133).
  &.data-cell.file.not-draggable:hover,
  &.data-cell.file.not-draggable:active {
    cursor: pointer;
  }

  &[data-status="archived"] {
    opacity: 0.5;
  }
`;

export const FileComponent = ({
  data,
  onClick,
}: {
  className?: string;
  data: File;
  onClick?: (file: File) => void;
}) => {
  const { handleFileClick } = useCaseFiles();
  const fileIconSrc = getFileIconSrc(data?.name);
  const [isDragging, setIsDragging] = useState(false);
  const draggable = !isManagedRow(data);

  const handleClick = () => {
    handleFileClick(data);
    onClick?.(data);
  };

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    e.dataTransfer.setData("text/plain", JSON.stringify(data));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <StyledContainer
      className={`data-cell file${draggable ? "" : " not-draggable"}`}
      title={data?.name}
      onClick={handleClick}
      data-status={data?.status}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onDragEnd={draggable ? handleDragEnd : undefined}
      style={{
        zIndex: isDragging ? 1000 : "auto",
      }}
    >
      {isEvidenceFile(data) ? (
        <ManagedRowIcon
          row={data}
          src={fileIconSrc}
          onError={handleFileIconError}
        />
      ) : (
        <img
          src={fileIconSrc}
          onError={handleFileIconError}
          alt="icon"
          style={{ height: "1.5rem" }}
        />
      )}
      <div className="label">{data?.name}</div>
    </StyledContainer>
  );
};
